/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/Try.ts
 * @create:      2025-10-14 10:47:43.678
 * @modify:      2025-10-14 22:42:15.321
 * @version:     6.1.1
 * @times:       3
 * @lines:       66
 * @copyright:   Copyright © 2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ILoggingAgent } from "../interface/ILoggingAgent";

/**
 * Safely executes a function and catches any exceptions or promise rejections.
 * 
 * - If the function executes successfully, its result is returned directly.
 * - If it throws (synchronously) or rejects (asynchronously),
 *   the provided `fallback` value is returned instead.
 * - If a `logger` is provided, any caught exception is logged using `logger.Warn(error)`.
 *
 * @param fn The function to execute. Can be synchronous or return a Promise.
 * @param fallback The value to return if an error occurs.
 * @param logger Optional logger that implements a `Warn(error)` method.
 * @returns The function's result if successful, otherwise the fallback value.
 *
 */
export function Try<T>(fn: () => T | Promise<T>, fallback: T, logger?: ILoggingAgent): T | Promise<T> {
    try {
        const result = fn();

        if (result instanceof Promise) {
            return result.catch((ex) => {
                logger?.Warn(ex);
                return fallback;
            });
        }

        return result;
    } catch (ex) {
        logger?.Warn(ex);
        return fallback;
    }
}
